import { useState, useEffect, useMemo } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { Phone, Building2, Users, RefreshCw, Search, User, Mail, Smartphone, Monitor, Filter, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type { MasterExtensionRow as ExtensionData, MasterExtensionStats as ExtensionStats } from '@easyflow/shared';
import { toErr } from '@easyflow/shared';

export default function ExtensionsMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [extensions, setExtensions] = useState<ExtensionData[]>([]);
  const [stats, setStats] = useState<ExtensionStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const uniqueTenants = useMemo(() => {
    const tenants = new Map<string, string>();
    extensions.forEach(ext => {
      if (!tenants.has(ext.tenant_id)) {
        tenants.set(ext.tenant_id, ext.tenant_name);
      }
    });
    return Array.from(tenants.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [extensions]);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const [extensionsRes, statsRes] = await Promise.all([
        apiClient.get<ExtensionData[]>('/master/extensions'),
        apiClient.get<ExtensionStats>('/master/extensions/stats'),
      ]);

      setExtensions(Array.isArray(extensionsRes.data) ? extensionsRes.data : []);
      setStats(statsRes.data ?? null);
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to load extensions',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredExtensions = useMemo(() => {
    return extensions.filter((ext) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        ext.extension.toLowerCase().includes(search) ||
        (ext.first_name ?? '').toLowerCase().includes(search) ||
        (ext.last_name ?? '').toLowerCase().includes(search) ||
        ext.tenant_name.toLowerCase().includes(search) ||
        (ext.email ?? '').toLowerCase().includes(search) ||
        (ext.legal_name || '').toLowerCase().includes(search) ||
        (ext.vat_number || '').toLowerCase().includes(search);
      
      const matchesTenant = tenantFilter === 'all' || ext.tenant_id === tenantFilter;
      const matchesStatus = statusFilter === 'all' || ext.status === statusFilter;
      
      return matchesSearch && matchesTenant && matchesStatus;
    });
  }, [extensions, searchTerm, tenantFilter, statusFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setTenantFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm !== '' || tenantFilter !== 'all' || statusFilter !== 'all';

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400">Active</Badge>
    ) : (
      <Badge variant="secondary" className="bg-muted text-muted-foreground">Inactive</Badge>
    );
  };

  const getDeviceTypeBadge = (type: string) => {
    const config = {
      softphone: { icon: Smartphone, color: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400', label: 'Softphone' },
      deskphone: { icon: Monitor, color: 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400', label: 'Deskphone' },
    };
    const device = config[type as keyof typeof config] || { icon: Phone, color: 'bg-muted text-foreground', label: type };
    const Icon = device.icon;
    
    return (
      <Badge className={device.color}>
        <Icon className="h-3 w-3 mr-1" />
        {device.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-muted dark:bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-muted rounded animate-pulse" />
        </div>
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={10} />
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent">
              Extensions Management
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Cross-tenant extensions overview and monitoring</p>
          </div>
          <Button onClick={loadData} variant="outline" className="border-border dark:border-white/20 text-foreground dark:text-muted-foreground/60">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Total Extensions
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.total_extensions}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Active Extensions
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-green-600 dark:text-green-400">{stats.active_extensions}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Tenants with Extensions
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.tenants_with_extensions}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-orange-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Softphones
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-orange-600 dark:text-orange-400">{stats.softphones}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>
          </div>
        )}

        {/* Extensions Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex flex-col gap-4">
              {/* Search and Filters Row */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[250px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 dark:text-muted-foreground/70 h-4 w-4" />
                  <Input
                    placeholder="Search by extension, name, tenant, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Tenant Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground/70 dark:text-muted-foreground/70" />
                  <Select value={tenantFilter} onValueChange={setTenantFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Tutti i Tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i Tenant</SelectItem>
                      {uniqueTenants.map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Tutti gli Stati" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli Stati</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground dark:text-muted-foreground/70 dark:hover:text-foreground">
                    <X className="h-4 w-4 mr-1" />
                    Cancella filtri
                  </Button>
                )}
              </div>

              {/* Results count */}
              <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">
                {filteredExtensions.length} di {extensions.length} estensioni
                {hasActiveFilters && ' (filtrate)'}
              </div>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border border-border dark:border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-border dark:border-white/10">
                    <TableHead className="text-foreground">Extension</TableHead>
                    <TableHead className="text-foreground">User</TableHead>
                    <TableHead className="text-foreground">Tenant</TableHead>
                    <TableHead className="text-foreground">Store</TableHead>
                    <TableHead className="text-foreground">Device Type</TableHead>
                    <TableHead className="text-foreground">Email</TableHead>
                    <TableHead className="text-foreground">Status</TableHead>
                    <TableHead className="text-foreground">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExtensions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                        No extensions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredExtensions.map((ext) => (
                      <TableRow key={ext.id} className="hover:bg-muted/50 dark:hover:bg-white/5 border-border dark:border-white/10">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground/70 dark:text-muted-foreground/70" />
                            <code className="px-2 py-1 bg-muted rounded text-sm text-foreground">
                              {ext.extension}
                            </code>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground/70 dark:text-muted-foreground/70" />
                            <div>
                              <div className="font-medium text-foreground">
                                {ext.first_name} {ext.last_name}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-foreground">{ext.tenant_name}</div>
                            <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">{ext.tenant_slug}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {ext.store_name ? (
                            <span className="text-sm text-muted-foreground dark:text-muted-foreground/60">{ext.store_name}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground/70 dark:text-muted-foreground italic">No store</span>
                          )}
                        </TableCell>
                        <TableCell>{getDeviceTypeBadge(ext.sip_device_type)}</TableCell>
                        <TableCell>
                          {ext.email ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground dark:text-muted-foreground/60">
                              <Mail className="h-3 w-3" />
                              {ext.email}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground/70 dark:text-muted-foreground italic">No email</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(ext.status)}</TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground dark:text-muted-foreground/60">
                            {new Date(ext.created_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
